import { useQuery } from "@tanstack/react-query"
import { AlertTriangle, Boxes, Layers, Maximize2, Minimize2, Network } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router"

import { api } from "@/api/cliente"
import { MAPA_2, MAPA_3, propio, unir } from "@/componentes/mapa/datos"
import type { DatosMapa, Grafo3D } from "@/componentes/mapa/grafo3d"
import { Pestanas } from "@/componentes/ui/Pestanas"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

type Vista = "tres" | "dos" | "unidos"

const PESTANAS = [
  { valor: "tres" as const, etiqueta: "Sistema 3.0", icono: Boxes },
  { valor: "dos" as const, etiqueta: "Sistema 2.0", icono: Layers },
  { valor: "unidos" as const, etiqueta: "Los dos unidos", icono: Network },
]

type Inventario = {
  archivos: { ruta: string; modificado: string }[]
  leido_en: string
  error: string | null
}

/** Lo que cambió en el 2.0 desde que se dibujó su mapa. Pura. */
export function cambiosDesde(
  inventario: Inventario["archivos"],
  conocidos: string[],
  dibujado: string,
): { nuevos: string[]; borrados: string[]; cambiados: string[] } {
  const vivos = new Set(inventario.map((a) => a.ruta))
  const antes = new Set(conocidos)
  const limite = new Date(dibujado).getTime()
  return {
    nuevos: inventario.filter((a) => !antes.has(a.ruta)).map((a) => a.ruta),
    borrados: conocidos.filter((r) => !vivos.has(r)),
    cambiados: inventario
      .filter((a) => antes.has(a.ruta) && new Date(a.modificado).getTime() > limite)
      .map((a) => a.ruta),
  }
}

/**
 * Mapa del sistema (24/10): los módulos del 3.0, los del 2.0 y los dos unidos, en un grafo
 * 3D al estilo de Obsidian (`componentes/mapa/`). Pulsar un módulo enseña qué recibe y a
 * qué alimenta.
 *
 * ⚠ **Se mantiene al día por dos lados.** El del 3.0 es un JSON del repositorio que una
 * prueba compara con el menú y el beat: un módulo nuevo sin su nodo no se despliega. El
 * del 2.0 lo vigila el servidor (`GET /mapa/vigencia`) contra los archivos del hosting, y
 * la pantalla avisa de lo que cambió allá después de dibujarlo.
 */
export function MapaSistema() {
  const [params, setParams] = useSearchParams()
  const vista: Vista =
    (["tres", "dos", "unidos"] as const).find((v) => v === params.get("vista")) ?? "tres"
  const datos: DatosMapa = useMemo(
    () => (vista === "tres" ? propio(MAPA_3) : vista === "dos" ? propio(MAPA_2) : unir()),
    [vista],
  )
  const actualizado = vista === "dos" ? MAPA_2.actualizado : MAPA_3.actualizado
  const [elegido, setElegido] = useState<string | null>(params.get("nodo"))
  const [visibles, setVisibles] = useState<Set<string>>(() => new Set(Object.keys(datos.areas)))
  const [sobre, setSobre] = useState<{ texto: string; x: number; y: number } | null>(null)
  const [busqueda, setBusqueda] = useState("")
  const lienzo = useRef<HTMLDivElement>(null)
  const marco = useRef<HTMLDivElement>(null)
  // (24/10) Pantalla completa: la del navegador si la hay; si no (iPhone), cubre la ventana.
  const [completa, setCompleta] = useState(false)
  const grafo = useRef<Grafo3D | null>(null)

  const inventario = useQuery({
    queryKey: ["mapa", "inventario-2"],
    queryFn: () => api.get<Inventario>("/api/v1/mapa/inventario-2"),
    staleTime: 10 * 60_000,
    enabled: vista !== "tres",
  })

  // three.js se carga aquí y sólo aquí; un grafo por vista.
  useEffect(() => {
    const caja = lienzo.current
    if (!caja) return
    let vivo = true
    setVisibles(new Set(Object.keys(datos.areas)))
    import("@/componentes/mapa/grafo3d").then(({ Grafo3D }) => {
      if (!vivo) return
      grafo.current = new Grafo3D(caja, datos, {
        onElegir: (id) => setElegido(id),
        onSobre: (texto, x, y) => setSobre(texto ? { texto, x, y } : null),
        rotularDesde: datos.areas["3.0"] ? 11 : 6,
      })
      const inicial = new URLSearchParams(location.search).get("nodo")
      if (inicial) grafo.current.elegir(inicial)
    })
    return () => {
      vivo = false
      grafo.current?.destruir()
      grafo.current = null
    }
  }, [datos])

  useEffect(() => {
    grafo.current?.elegir(elegido)
    setParams(
      (p) => {
        const nuevos = new URLSearchParams(p)
        if (elegido) nuevos.set("nodo", elegido)
        else nuevos.delete("nodo")
        return nuevos
      },
      { replace: true },
    )
  }, [elegido, setParams])

  useEffect(() => {
    grafo.current?.areas(visibles)
  }, [visibles])

  useEffect(() => {
    const alCambiar = () => setCompleta(document.fullscreenElement === marco.current)
    const alTeclear = (ev: KeyboardEvent) => {
      if (ev.key === "Escape" && !document.fullscreenElement) setCompleta(false)
    }
    document.addEventListener("fullscreenchange", alCambiar)
    document.addEventListener("keydown", alTeclear)
    return () => {
      document.removeEventListener("fullscreenchange", alCambiar)
      document.removeEventListener("keydown", alTeclear)
    }
  }, [])

  async function alternarCompleta() {
    const el = marco.current
    if (!el) return
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => undefined)
      return
    }
    if (completa) {
      setCompleta(false)
      return
    }
    if (document.fullscreenEnabled && el.requestFullscreen) {
      try {
        await el.requestFullscreen()
        return
      } catch {
        // Sigue abajo: cubrir la ventana.
      }
    }
    setCompleta(true)
  }

  const porId = useMemo(() => new Map(datos.nodos.map((n) => [n.id, n])), [datos])
  const grado = useMemo(() => {
    const g = new Map<string, number>()
    for (const f of datos.flujos) {
      g.set(f.de, (g.get(f.de) ?? 0) + 1)
      g.set(f.a, (g.get(f.a) ?? 0) + 1)
    }
    return g
  }, [datos])
  const nodo = elegido ? porId.get(elegido) : undefined
  const recibe = nodo ? datos.flujos.filter((f) => f.a === nodo.id && porId.has(f.de)) : []
  const alimenta = nodo ? datos.flujos.filter((f) => f.de === nodo.id && porId.has(f.a)) : []
  const colorDe = (id: string) => {
    const n = porId.get(id)
    return n?.color ?? datos.areas[n?.area ?? ""]?.color ?? "gray"
  }
  const centrales = [...datos.nodos]
    .sort((a, b) => (grado.get(b.id) ?? 0) - (grado.get(a.id) ?? 0))
    .slice(0, 6)
  const v2 =
    inventario.data && !inventario.data.error && MAPA_2.archivos
      ? cambiosDesde(
          inventario.data.archivos,
          MAPA_2.archivos,
          MAPA_2.dibujado ?? MAPA_2.actualizado,
        )
      : null
  const cambios = v2 ? v2.nuevos.length + v2.borrados.length + v2.cambiados.length : 0

  function buscar(texto: string) {
    setBusqueda(texto)
    const q = texto.trim().toLowerCase()
    const n = datos.nodos.find((x) => x.nombre.toLowerCase() === q)
    if (n) {
      setVisibles((v) => new Set([...v, n.area]))
      setElegido(n.id)
    }
  }

  const fila = (id: string, txt: string, flecha: string) => (
    <li key={`${flecha}-${id}-${txt}`}>
      <button
        type="button"
        onClick={() => setElegido(id)}
        className="foco-cristal toque-cristal grid w-full grid-cols-[0.75rem_1fr] gap-x-2 rounded-[var(--radius-control)] p-2 text-left text-sm"
      >
        <i className="mt-1.5 size-2.5 rounded-full" style={{ background: colorDe(id) }} />
        <span className="font-medium">
          <span style={{ color: "var(--primary-texto)" }}>{flecha} </span>
          {porId.get(id)?.nombre}
        </span>
        <span className="col-start-2 text-xs" style={{ color: "var(--fg-muted)" }}>
          {txt}
        </span>
      </button>
    </li>
  )

  return (
    <div className="space-y-4">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <Network size={20} aria-hidden={true} /> Mapa del sistema
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--fg-muted)" }}>
          Cada esfera es un módulo; las líneas, los flujos de datos, y las luces van en el sentido
          en que viaja la información. Dibujado el {comoFecha(actualizado)}.
        </p>
      </header>

      <Pestanas
        pestanas={PESTANAS}
        activa={vista}
        onCambiar={(v) => {
          setElegido(null)
          setParams({ vista: v }, { replace: true })
        }}
        etiqueta="Qué sistema"
        idPanel="panel-mapa"
        prefijoId="pestana-mapa"
      />

      {vista !== "tres" && cambios > 0 && v2 && (
        <p
          role="status"
          className="tarjeta flex items-start gap-2 p-3 text-sm"
          style={{ borderColor: "var(--warning)" }}
        >
          <AlertTriangle
            size={16}
            className="mt-0.5 shrink-0"
            aria-hidden={true}
            style={{ color: "var(--warning)" }}
          />
          <span>
            El 2.0 cambió después de dibujar su mapa ({comoFecha(MAPA_2.actualizado)}):{" "}
            {[
              v2.nuevos.length ? `${v2.nuevos.length} archivo(s) nuevo(s)` : "",
              v2.borrados.length ? `${v2.borrados.length} retirado(s)` : "",
              v2.cambiados.length ? `${v2.cambiados.length} modificado(s)` : "",
            ]
              .filter(Boolean)
              .join(", ")}
            . Revisa si cambian sus flujos.
            <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
              {[...v2.nuevos, ...v2.cambiados, ...v2.borrados].slice(0, 12).join(" · ")}
              {cambios > 12 ? " …" : ""} — leído el {comoFechaHora(inventario.data?.leido_en ?? "")}
            </span>
          </span>
        </p>
      )}

      <div
        id="panel-mapa"
        ref={marco}
        role="tabpanel"
        aria-labelledby={`pestana-mapa-${vista}`}
        className={`grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] ${
          completa ? "fixed inset-0 z-50 p-3 zona-segura" : ""
        }`}
        style={
          completa
            ? { background: "radial-gradient(ellipse at 50% 40%, rgb(30 10 19), rgb(8 4 6))" }
            : undefined
        }
      >
        <div
          className={`relative overflow-hidden rounded-[var(--radius-tarjeta)] ${
            completa ? "h-[calc(100dvh-1.5rem)] max-lg:h-[60dvh]" : "h-[62dvh] min-h-[420px]"
          }`}
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, rgb(40 14 26 / 0.85), rgb(10 5 8 / 0.95))",
          }}
        >
          <div
            ref={lienzo}
            className="absolute inset-0"
            role="img"
            aria-label="Grafo 3D de módulos y flujos"
          />
          <button
            type="button"
            onClick={alternarCompleta}
            aria-pressed={completa}
            className="foco-cristal absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-[var(--radius-control)] px-2.5 py-1.5 text-xs"
            style={{
              background: "rgb(20 10 15 / 0.85)",
              border: "1px solid rgb(242 221 166 / 0.25)",
              color: "rgb(243 236 230)",
            }}
          >
            {completa ? (
              <Minimize2 size={14} aria-hidden={true} />
            ) : (
              <Maximize2 size={14} aria-hidden={true} />
            )}
            {completa ? "Salir de pantalla completa" : "Pantalla completa"}
          </button>
          <div className="pointer-events-none absolute left-3 right-48 top-3 flex flex-col gap-2 max-sm:right-3 max-sm:top-14">
            <input
              className="campo pointer-events-auto h-9 w-full max-w-xs text-sm"
              list="mapa-nombres"
              placeholder="Buscar un módulo…"
              aria-label="Buscar un módulo"
              value={busqueda}
              onChange={(e) => buscar(e.target.value)}
            />
            <datalist id="mapa-nombres">
              {datos.nodos.map((n) => (
                <option key={n.id} value={n.nombre} />
              ))}
            </datalist>
            <div className="pointer-events-auto flex flex-wrap gap-1.5">
              {Object.entries(datos.areas).map(([clave, a]) => (
                <button
                  key={clave}
                  type="button"
                  aria-pressed={visibles.has(clave)}
                  onClick={() =>
                    setVisibles((v) => {
                      const n = new Set(v)
                      if (n.has(clave) && n.size > 1) n.delete(clave)
                      else n.add(clave)
                      return n
                    })
                  }
                  className="foco-cristal inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs"
                  style={{
                    background: "rgb(20 10 15 / 0.8)",
                    border: "1px solid rgb(242 221 166 / 0.18)",
                    color: "rgb(243 236 230)",
                    opacity: visibles.has(clave) ? 1 : 0.4,
                  }}
                >
                  <i className="size-2 rounded-full" style={{ background: a.color }} />
                  {a.nombre}
                </button>
              ))}
            </div>
          </div>
        </div>

        <aside
          className={`tarjeta space-y-3 overflow-y-auto p-4 ${
            completa
              ? "max-h-[calc(100dvh-1.5rem)] max-lg:max-h-[calc(40dvh-2.5rem)]"
              : "max-h-[62dvh] min-h-[420px]"
          }`}
          aria-live="polite"
        >
          {!nodo ? (
            <>
              <h2 className="text-base font-semibold">Cómo leer el mapa</h2>
              <p className="text-sm">
                Pulsa una esfera o búscala. Arrastra para girar; rueda o pellizco para acercarte.
                Esc o un clic en el vacío vuelven a la vista general.
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                {[
                  [datos.nodos.length, "módulos"],
                  [datos.flujos.length, "flujos"],
                  [Object.keys(datos.areas).length, vista === "unidos" ? "sistemas" : "áreas"],
                ].map(([n, t]) => (
                  <div
                    key={t}
                    className="rounded-[var(--radius-control)] border p-2"
                    style={{ borderColor: "var(--border)" }}
                  >
                    <b className="block text-lg tabular-nums">{n}</b>
                    <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
                      {t}
                    </span>
                  </div>
                ))}
              </div>
              <h3 className="text-xs font-semibold uppercase" style={{ color: "var(--fg-muted)" }}>
                Los más conectados
              </h3>
              <ul className="space-y-1">
                {centrales.map((n) => fila(n.id, `${grado.get(n.id) ?? 0} flujos`, "•"))}
              </ul>
            </>
          ) : (
            <>
              <button
                type="button"
                className="foco-cristal toque-cristal rounded-[var(--radius-control)] px-2 py-1 text-xs"
                onClick={() => setElegido(null)}
              >
                ← Ver todo el mapa
              </button>
              <span
                className="inline-block rounded border px-2 py-0.5 text-xs uppercase"
                style={{ color: colorDe(nodo.id), borderColor: colorDe(nodo.id) }}
              >
                {datos.areas[nodo.area]?.nombre}
              </span>
              <h2 className="text-lg font-semibold">{nodo.nombre}</h2>
              <p className="text-sm">{nodo.desc}</p>
              {recibe.length > 0 && (
                <>
                  <h3
                    className="text-xs font-semibold uppercase"
                    style={{ color: "var(--fg-muted)" }}
                  >
                    Recibe de · {recibe.length}
                  </h3>
                  <ul className="space-y-1">{recibe.map((f) => fila(f.de, f.txt, "←"))}</ul>
                </>
              )}
              {alimenta.length > 0 && (
                <>
                  <h3
                    className="text-xs font-semibold uppercase"
                    style={{ color: "var(--fg-muted)" }}
                  >
                    Alimenta a · {alimenta.length}
                  </h3>
                  <ul className="space-y-1">{alimenta.map((f) => fila(f.a, f.txt, "→"))}</ul>
                </>
              )}
            </>
          )}
        </aside>
        {sobre && (
          <div
            className="pointer-events-none fixed z-50 rounded px-2 py-1 text-xs"
            style={{
              left: sobre.x + 14,
              top: sobre.y + 14,
              background: "var(--surface)",
              border: "1px solid var(--border)",
            }}
          >
            {sobre.texto}
          </div>
        )}
      </div>
    </div>
  )
}
